import { useEffect, useState } from "react";
import { motion, animate } from "framer-motion";

/**
 * Professional animated radial gauge for the Trade Opportunity Score.
 * - Smooth arc fill (spring-eased) + count-up number.
 * - Threshold notch shows exactly where a trade fires.
 * - Soft glow that gently pulses while the engine is WAITING.
 */
export function ScoreGauge({ score = 0, threshold = 65, decision = "WAIT" }) {
  const size = 132;
  const stroke = 9;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const color = pct >= 80 ? "#22e07b" : pct >= 65 ? "var(--sfx-accent)" : "#f5a623";
  const waiting = decision === "WAIT";
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const controls = animate(0, pct, {
      duration: 1.1,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [pct]);

  // threshold notch position on the ring (ring starts at top, clockwise)
  const tAng = (Math.min(100, Math.max(0, threshold)) / 100) * 2 * Math.PI - Math.PI / 2;
  const tx = size / 2 + r * Math.cos(tAng);
  const ty = size / 2 + r * Math.sin(tAng);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="score-gauge">
      {/* soft glow */}
      <motion.div
        className="absolute rounded-full"
        style={{ inset: 14, backgroundColor: color, filter: "blur(22px)" }}
        animate={{ opacity: waiting ? [0.12, 0.28, 0.12] : 0.22 }}
        transition={{ duration: 2.4, repeat: waiting ? Infinity : 0, ease: "easeInOut" }}
      />
      <svg width={size} height={size} className="relative" style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#0d1930" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          style={{ stroke: color }}
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ - (circ * pct) / 100 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        />
      </svg>
      {/* threshold notch (white tick where a trade fires) */}
      <div
        className="absolute h-[7px] w-[7px] rounded-full bg-white ring-2 ring-[#060c18]"
        style={{ left: tx - 3.5, top: ty - 3.5 }}
        title={`Trade fires at ${threshold}`}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading font-bold text-[34px] leading-none tabular-nums"
          data-testid="opportunity-score" style={{ color }}>
          {display}
        </span>
        <span className="font-mono-term text-[10px] text-[#5f7ba6] mt-1 tracking-widest">/ 100</span>
        <span className="font-mono-term text-[9px] text-[#5f7ba6] mt-1 uppercase tracking-widest">
          fires at {threshold}
        </span>
      </div>
    </div>
  );
}
